"use client";

import type { Permission } from "@/types";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Check a catalogue app permission from GET /rbac/apps/{app}/me.
 */
export function usePermission(permission: Permission): boolean {
  const permissions = useAuthStore((s) => s.appAccess?.permissions);
  if (!permissions?.length) return false;
  return permissions.some((p) => 
    p === permission || 
    p === "*" || 
    (p.endsWith(".*") && permission.startsWith(p.replace(".*", "")))
  );
}

const EMPTY_PERMISSIONS: Permission[] = [];

export function usePermissions(): Permission[] {
  return useAuthStore((s) => s.appAccess?.permissions || EMPTY_PERMISSIONS);
}

/** Draft/pending/approved/rejected lists — viewers only have published. */
export function useCanViewNonPublished(): boolean {
  const canViewAll = usePermission("property.view.all");
  const canViewOwn = usePermission("property.view.own");
  return canViewAll || canViewOwn;
}

/** True when the user can manage ACL (any acl.* permission). */
export function useCanManageAcl(): boolean {
  const permissions = usePermissions();
  return permissions.some(
    (p) =>
      p === "acl.manage" ||
      p === "acl.manage_privileged" ||
      p.startsWith("acl.user.") ||
      p.startsWith("acl.role."),
  );
}
